import { Component, Injector, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { AppWizService } from 'src/app/core/services/app-wiz.service';
import { CertificateService } from 'src/app/core/services/certificate.service';
import { UserService } from 'src/app/core/services/user.service';
import { ArtifactBaseComponent } from '../artifact-base-component';
import { Certification } from 'src/app/core/models/certification.model';

@Component({
  selector: 'app-cert-person-lookup',
  templateUrl: './cert-person-lookup.component.html',
  styleUrls: ['./cert-person-lookup.component.scss']
})
export class CertPersonLookupComponent extends ArtifactBaseComponent implements OnInit {
  certificates: Certification[] = [];
  isLoadingCerts = false;
  isLoadingUser = false;
  searchError = '';
  formAlsdeId = '';
  person: any = null;

  constructor(
    private injector: Injector,
    private fb: FormBuilder,
    public override appWizService: AppWizService,
    private userService: UserService,
    private certService: CertificateService
  ) {
    super(injector);
    this.interalForm = this.fb.group({
      alsdeId: ['', [Validators.required]],
    });
    this.interalForm.valueChanges.subscribe(this.validateSection.bind(this));
  }

  initJsonData() {
    this.jsonData = this.jsonData || { person: {}, certificates: [] };
    this.jsonData.person = this.jsonData.person || {};
    this.jsonData.certificates = this.jsonData.certificates || [];
    this.certificates = this.jsonData.certificates;
    this.person = this.jsonData.person;
    this.formAlsdeId = this.person?.demographics?.alsdeId || '';
    this.interalForm.patchValue({ alsdeId: this.formAlsdeId });
  }

  validateJsonData() {
    this.artifact.sectionValid = this.interalForm.valid;
  }

  resetJsonData() {
    this.jsonData = { person: {}, certificates: [] };
    this.person = null;
    this.certificates = [];
    this.formAlsdeId = '';
    this.searchError = '';
    this.interalForm.reset();
  }

  searchUser() {
    this.searchError = '';
    this.isLoadingUser = true;
    this.userService.getUserDemographics(this.formAlsdeId).subscribe({
      next: (user: any) => {
        this.isLoadingUser = false;
        this.setUser(user);
      },
      error: (err) => {
        this.isLoadingUser = false;
        const status = err.status;
        let msg: string;
        if (status === 502 || status === 503 || status === 504) {
          msg = 'The lookup service is temporarily unavailable. Please try again later.';
        } else if (status === 404) {
          msg = 'No user found with the provided ALSDE ID.';
        } else if (typeof err.error === 'string' && !err.error.startsWith('Http')) {
          msg = err.error;
        } else {
          msg = err.error?.detail || err.error?.title || 'An error occurred during lookup. Please try again.';
        }
        // Break ' - section' lists onto their own lines for readability (paired with white-space:pre-line in the template).
        this.searchError = msg.replace(/ - /g, '\n- ');
        this.setUser(null);
      }
    });
  }

  private setUser(user: any) {
    this.person = user;
    this.jsonData = { person: user || {}, certificates: [] };
    this.certificates = [];

    const alsdeId = user?.demographics?.alsdeId;
    if (alsdeId) {
      this.interalForm.patchValue({ alsdeId });
      this.loadCertificates(alsdeId);
    } else {
      this.interalForm.patchValue({ alsdeId: '' });
    }

    this.validateSection();
  }

  private loadCertificates(alsdeId: string) {
    this.isLoadingCerts = true;
    this.certService.getCertificatesByAlsdeId(alsdeId).subscribe({
      next: (certs: Certification[]) => {
        this.certificates = certs;
        this.jsonData.certificates = certs.map(c => c.toJSON());
        this.isLoadingCerts = false;
      },
      error: () => {
        this.certificates = [];
        this.jsonData.certificates = [];
        this.isLoadingCerts = false;
      }
    });
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.searchUser();
    }
    this.searchError = '';
  }
}
